import Menu from '@/global/components/MenuV2'
import ContentGuide from '@/global/guides/ContentGuide'
import { cn } from '@/lib/utils'
import {
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    InputAdornment,
} from '@mui/material'
import { FormField, FormFieldCheckbox } from 'global/components/Form'
import { FormRadioGroup } from 'global/components/Form/RadioGroup'
import IconeAjuda from 'global/components/IconeAjuda'
import React, { useCallback, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { PorcentagemRegraCalculo } from 'service/api'
import { match } from 'ts-pattern'
import { horasExtrasModel, IHorasExtras } from '../../constants'

type TipoHorasExcedentes = 'DIARIO' | 'SEMANAL' | 'MENSAL'
const DIAS_UTEIS = ['seg', 'ter', 'qua', 'qui', 'sex']

function Card({
    children,
    className,
}: {
    children: React.ReactNode
    className?: string
}) {
    return (
        <ContentGuide.Card
            className={cn(
                'h-fit flex-row max-w-full gap-2 rounded-sm',
                className,
            )}
        >
            {children}
        </ContentGuide.Card>
    )
}

export default function HorasExtras() {
    const {
        watch,
        getValues,
        formState: { errors },
        setValue,
    } = useFormContext()

    const [horasExtrasFeriado, porcentagem, configFeriado] = watch([
        'horasExtrasFeriado',
        'porcentagens',
        'horasExtras.fer',
    ]) as [boolean, PorcentagemRegraCalculo[], IHorasExtras]

    const tipoHorasExcedentes: TipoHorasExcedentes = watch(
        'tipoHorasExcedentes',
        'DIARIO',
    )

    useEffect(() => {
        if (!horasExtrasFeriado) {
            setValue('horasExtras.fer', {
                ...configFeriado,
                porcentagemDemaisHrs: 0,
                porcentagemPrimeirasHrs: 0,
            })
        }
    }, [porcentagem.map(p => p.porcentagem).join(', ')])

    const [editarSemana, setEditarSemana] = React.useState(() => {
        const semExists = Object.keys(getValues('horasExtras') ?? {}).includes(
            'sem',
        )

        return !semExists
    })

    const handleChange = (_: any, checked: boolean) => {
        setEditarSemana(checked)
    }

    const domingo = () => (
        <>
            <Grid item xs={30}>
                <Divider sx={{ marginY: 2 }} />
            </Grid>
            <Grid item sm={5 + 3} xs={24 + 6}>
                <span className="text-nowrap text-sm font-semibold">
                    Domingo
                </span>
            </Grid>
            <Grid item sm={10} xs={30}>
                <Card>
                    <FormField
                        size="small"
                        name={`horasExtras[dom].qtdPrimeirasHrs`}
                        label="Primeiras"
                        disabled={watch(`horasExtras[dom].todas`, false)}
                        required
                        type="time"
                        unHelperText
                        InputLabelProps={{
                            shrink: true,
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="start">
                                    h
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '[type="time"]::-webkit-calendar-picker-indicator':
                                {
                                    display: 'none',
                                },
                        }}
                    />
                    <FormField
                        size="small"
                        select
                        required
                        name={`horasExtras[dom].porcentagemPrimeirasHrs`}
                        label="Será"
                        unHelperText
                    >
                        {porcentagem.map(item => (
                            <Menu.Item key={item.id} value={item.porcentagem}>
                                {item.porcentagem}%
                            </Menu.Item>
                        ))}
                    </FormField>
                </Card>
            </Grid>
            <Grid item sm={7} xs={10}>
                <Card>
                    <FormField
                        size="small"
                        select
                        required
                        name={`horasExtras[dom].porcentagemDemaisHrs`}
                        disabled={watch(`horasExtras[dom].todas`, false)}
                        label="Demais"
                        unHelperText
                    >
                        {porcentagem.map(item => (
                            <Menu.Item key={item.id} value={item.porcentagem}>
                                {item.porcentagem}%
                            </Menu.Item>
                        ))}
                    </FormField>
                </Card>
            </Grid>
            <Grid item sm={5} xs={20}>
                <Card>
                    <FormField
                        name={`horasExtras[dom].prioridadeCompensacao`}
                        required
                        select
                        size="small"
                        label="Prioridade"
                        defaultValue="maior"
                        unHelperText
                    >
                        <Menu.Item value="maior">Maior</Menu.Item>
                        <Menu.Item value="menor">Menor</Menu.Item>
                    </FormField>
                </Card>
            </Grid>
        </>
    )

    return (
        <>
            <div className="flex justify-end items-end mb-2">
                <FormRadioGroup
                    row
                    name="tipoHorasExcedentes"
                    label="Configuração das horas extendidas"
                    defaultValue="DIARIO"
                    options={[
                        { value: 'DIARIO', label: 'Diário' },
                        { value: 'SEMANAL', label: 'Semanal' },
                        { value: 'MENSAL', label: 'Mensal' },
                    ]}
                    className="focus:text-inherit!"
                />
            </div>
            {match(tipoHorasExcedentes)
                .with('DIARIO', () => (
                    <RenderHorasExtras
                        editarSemana={editarSemana}
                        porcentagem={porcentagem}
                        tipoHorasExcedentes={tipoHorasExcedentes}
                        handleChange={handleChange}
                    />
                ))
                .with('SEMANAL', () => (
                    <Grid
                        container
                        alignItems="center"
                        spacing={1}
                        columns={30}
                    >
                        <Grid item sm={5 + 3} xs={24 + 6}>
                            <span className="text-nowrap text-sm font-semibold">
                                Semana
                            </span>
                        </Grid>
                        <Grid item sm={10} xs={30}>
                            <Card>
                                <FormField
                                    size="small"
                                    name={`horasExtras.semanal.qtdPrimeirasHrs`}
                                    label="Primeiras"
                                    required
                                    options={{
                                        numericOnly: true,
                                        blocks: [2, 2],
                                        delimiter: ':',
                                    }}
                                    unHelperText
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    onBlur={() => {
                                        setValue(
                                            'horasExtras.semanal.qtdPrimeirasHrs',
                                            getValues<string>(
                                                'horasExtras.semanal.qtdPrimeirasHrs',
                                            ).padEnd(5, '0'),
                                        )
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="start">
                                                h
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <FormField
                                    size="small"
                                    select
                                    required
                                    name={`horasExtras.semanal.porcentagemPrimeirasHrs`}
                                    label="Será"
                                    unHelperText
                                >
                                    {porcentagem.map(item => (
                                        <Menu.Item
                                            key={item.id}
                                            value={item.porcentagem}
                                        >
                                            {item.porcentagem}%
                                        </Menu.Item>
                                    ))}
                                </FormField>
                            </Card>
                        </Grid>
                        <Grid item sm={7} xs={10}>
                            <Card>
                                <FormField
                                    size="small"
                                    select
                                    required
                                    name={`horasExtras.semanal.porcentagemDemaisHrs`}
                                    label="Demais"
                                    unHelperText
                                >
                                    {porcentagem.map(item => (
                                        <Menu.Item
                                            key={item.id}
                                            value={item.porcentagem}
                                        >
                                            {item.porcentagem}%
                                        </Menu.Item>
                                    ))}
                                </FormField>
                            </Card>
                        </Grid>
                        <Grid item sm={5} xs={20}>
                            <Card>
                                <FormField
                                    name={`horasExtras.semanal.prioridadeCompensacao`}
                                    required
                                    select
                                    size="small"
                                    label="Prioridade"
                                    defaultValue="maior"
                                    unHelperText
                                >
                                    <Menu.Item value="maior">Maior</Menu.Item>
                                    <Menu.Item value="menor">Menor</Menu.Item>
                                </FormField>
                            </Card>
                        </Grid>
                        {domingo()}
                    </Grid>
                ))
                .with('MENSAL', () => (
                    <Grid
                        container
                        alignItems="center"
                        spacing={1}
                        columns={30}
                    >
                        <Grid item sm={5 + 3} xs={24 + 6}>
                            <span className="text-nowrap font-semibold text-sm">
                                Mensal
                            </span>
                        </Grid>
                        <Grid item sm={10} xs={30}>
                            <Card>
                                <FormField
                                    size="small"
                                    name={`horasExtras.mensal.qtdPrimeirasHrs`}
                                    label="Primeiras"
                                    required
                                    options={{
                                        numericOnly: true,
                                        blocks: [2, 2],
                                        delimiter: ':',
                                    }}
                                    unHelperText
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    onBlur={() => {
                                        setValue(
                                            'horasExtras.mensal.qtdPrimeirasHrs',
                                            getValues<string>(
                                                'horasExtras.mensal.qtdPrimeirasHrs',
                                            ).padEnd(5, '0'),
                                        )
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="start">
                                                h
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <FormField
                                    size="small"
                                    select
                                    required
                                    name={`horasExtras.mensal.porcentagemPrimeirasHrs`}
                                    label="Será"
                                    unHelperText
                                >
                                    {porcentagem.map(item => (
                                        <Menu.Item
                                            key={item.id}
                                            value={item.porcentagem}
                                        >
                                            {item.porcentagem}%
                                        </Menu.Item>
                                    ))}
                                </FormField>
                            </Card>
                        </Grid>
                        <Grid item sm={7} xs={10}>
                            <Card>
                                <FormField
                                    size="small"
                                    select
                                    required
                                    name={`horasExtras.mensal.porcentagemDemaisHrs`}
                                    label="Demais"
                                    unHelperText
                                >
                                    {porcentagem.map(item => (
                                        <Menu.Item
                                            key={item.id}
                                            value={item.porcentagem}
                                        >
                                            {item.porcentagem}%
                                        </Menu.Item>
                                    ))}
                                </FormField>
                            </Card>
                        </Grid>
                        <Grid item sm={5} xs={20}>
                            <Card>
                                <FormField
                                    name={`horasExtras.mensal.prioridadeCompensacao`}
                                    required
                                    select
                                    size="small"
                                    label="Prioridade"
                                    defaultValue="maior"
                                    unHelperText
                                >
                                    <Menu.Item value="maior">Maior</Menu.Item>
                                    <Menu.Item value="menor">Menor</Menu.Item>
                                </FormField>
                            </Card>
                        </Grid>
                        {domingo()}
                    </Grid>
                ))
                .exhaustive()}
            <Divider sx={{ marginY: 2 }} />
            <div className="flex justify-end items-center mt-4 mb-2">
                <FormFieldCheckbox
                    name="horasExtrasFeriado"
                    label="Horas extras no feriado"
                    onChange={(_, checked) => {
                        if (checked)
                            setValue('horasExtras.fer', {
                                ...configFeriado,
                                porcentagemDemaisHrs: 0,
                                porcentagemPrimeirasHrs: 0,
                            })
                    }}
                />
                <IconeAjuda>
                    Horas extras no feriado habilitadas por padrão. Desabilite
                    se não aplicável.
                </IconeAjuda>
            </div>
            <Linha
                disabled={!horasExtrasFeriado}
                item={horasExtrasModel.horasExtras.fer}
                tipoHorasExcedentes={tipoHorasExcedentes}
                editar={true}
                key={
                    horasExtrasFeriado
                        ? porcentagem.map(p => p.id).join(', ')
                        : 'fer'
                }
                index={'fer'}
                porcentagem={porcentagem}
            >
                <div className="w-full flex justify-end my-2">
                    <CheckboxControlePorTurno dia="fer" />
                </div>
            </Linha>
            {!!errors.horasExtras && (
                <span className="text-error text-xs font-semibold">
                    {errors.horasExtras.root?.message?.toString() ??
                        errors.horasExtras.message?.toString()}
                </span>
            )}
        </>
    )
}

function RenderHorasExtras({
    editarSemana,
    handleChange,
    porcentagem,
    tipoHorasExcedentes,
}: {
    editarSemana: boolean
    handleChange: (_: any, checked: boolean) => void
    porcentagem: PorcentagemRegraCalculo[]
    tipoHorasExcedentes: TipoHorasExcedentes
}) {
    return (
        <>
            {Object.entries(horasExtrasModel.horasExtras).map(
                ([key, item], index) =>
                    ['sem', 'sab', 'dom'].includes(key) && (
                        <React.Fragment key={key}>
                            {index !== 0 && <Divider sx={{ marginY: 2 }} />}
                            <Linha
                                editar={index === 0 ? !editarSemana : true}
                                item={item}
                                index={key}
                                porcentagem={porcentagem}
                                tipoHorasExcedentes={tipoHorasExcedentes}
                            >
                                {key === 'sem' ? (
                                    <SemanaComponent
                                        editarSemana={editarSemana}
                                        handleChange={handleChange}
                                        porcentagem={porcentagem}
                                        tipoHorasExcedentes={
                                            tipoHorasExcedentes
                                        }
                                    />
                                ) : (
                                    <div className="w-full flex justify-end my-2">
                                        <CheckboxControlePorTurno dia={key} />
                                    </div>
                                )}
                            </Linha>
                        </React.Fragment>
                    ),
            )}
        </>
    )
}

function SemanaComponent({
    editarSemana,
    handleChange,
    porcentagem,
    tipoHorasExcedentes,
}: {
    editarSemana: boolean
    handleChange: (_: any, checked: boolean) => void
    porcentagem: PorcentagemRegraCalculo[]
    tipoHorasExcedentes: TipoHorasExcedentes
}) {
    return (
        <>
            <div className="flex w-full justify-end items-center mt-4">
                <CheckboxControlePorTurno dia="sem" disabled={editarSemana} />
                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                        ml: 1,
                        mr: 3,
                    }}
                />
                <FormControlLabel
                    control={<Checkbox id="editarSemana" />}
                    checked={editarSemana}
                    onChange={handleChange}
                    labelPlacement="end"
                    label={
                        <span className="inline-flex items-center select-none">
                            Editar semana
                            <IconeAjuda className="ml-2 -mr-3">
                                <div className="w-52">
                                    Habilite para editar os dias de segunda a
                                    sexta-feira separadamente.
                                </div>
                            </IconeAjuda>
                        </span>
                    }
                />
            </div>
            {editarSemana && (
                <div className="flex flex-col gap-2 p-2 w-full">
                    {Object.entries(horasExtrasModel.horasExtras).map(
                        ([subKey, subItem]) =>
                            DIAS_UTEIS.includes(subKey) && (
                                <React.Fragment key={subKey}>
                                    <Divider sx={{ mb: 2 }} />
                                    <Linha
                                        editar={editarSemana}
                                        item={subItem}
                                        index={subKey}
                                        porcentagem={porcentagem}
                                        tipoHorasExcedentes={
                                            tipoHorasExcedentes
                                        }
                                    >
                                        <div className="w-full flex justify-end my-2">
                                            <CheckboxControlePorTurno
                                                dia={subKey}
                                            />
                                        </div>
                                    </Linha>
                                </React.Fragment>
                            ),
                    )}
                </div>
            )}
        </>
    )
}

interface LinhaProps {
    item: IHorasExtras
    index: string
    editar: boolean
    onChange?: (name: string, value: unknown) => void
    porcentagem: PorcentagemRegraCalculo[]
    disabled?: boolean
    children?: React.ReactNode
    tipoHorasExcedentes: TipoHorasExcedentes
}

const Linha: React.FC<LinhaProps> = ({
    item,
    index,
    editar,
    porcentagem,
    children,
    disabled = false,
    tipoHorasExcedentes,
}) => {
    const { watch, setValue } = useFormContext()

    const isTodas = useCallback(
        (key: string) => {
            return watch(`horasExtras[${key}].todas`, false)
        },
        [watch],
    )

    const isTurno = useCallback(
        (index: string) => {
            return watch(`horasExtras[${index}].controleTurno`, false)
        },
        [watch],
    )

    function handleChangeSemana(field: string, value: any) {
        if (index === 'sem') {
            DIAS_UTEIS.forEach(dia => {
                setValue(`horasExtras[${dia}].${field}`, value)
            })
        }
    }

    const isFeriadoNaoDiario =
        index === 'fer' && tipoHorasExcedentes !== 'DIARIO'

    return (
        <Grid container alignItems="center" spacing={1} columns={30}>
            <Grid item sm={5} xs={24}>
                <span className="text-nowrap text-sm font-semibold">
                    {item.nome}
                </span>
            </Grid>
            <Grid item sm={3} xs={6}>
                {isFeriadoNaoDiario ? (
                    <div className="w-full" />
                ) : (
                    <FormFieldCheckbox
                        name={`horasExtras[${index}].todas`}
                        label="Todas"
                        disabled={!editar || disabled}
                        onChange={(_, checked) => {
                            handleChangeSemana('todas', !checked)
                            if (!checked) {
                                setValue(
                                    `horasExtras[${index}].porcentagemDemaisHrs`,
                                    0,
                                )
                                setValue(
                                    `horasExtras[${index}].qtdPrimeirasHrs`,
                                    '',
                                )
                                handleChangeSemana('porcentagemDemaisHrs', 0)
                                handleChangeSemana('qtdPrimeirasHrs', '')

                                setValue(
                                    `horasExtras[${index}].controleTurno`,
                                    false,
                                )
                                handleChangeSemana('controleTurno', false)
                            }
                        }}
                    />
                )}
            </Grid>
            <Grid item sm={10} xs={30}>
                <Card>
                    <FormField
                        size="small"
                        disabled={
                            !editar ||
                            isTodas(index) ||
                            disabled ||
                            isTurno(index)
                        }
                        name={`horasExtras[${index}].qtdPrimeirasHrs`}
                        label={isTurno(index) ? 'Primeiro turno' : 'Primeiras'}
                        required={!isTodas(index) && !isTurno(index)}
                        type="time"
                        unHelperText
                        InputLabelProps={{
                            shrink: true,
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="start">
                                    h
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '[type="time"]::-webkit-calendar-picker-indicator':
                                {
                                    display: 'none',
                                },
                        }}
                        onChange={v => {
                            handleChangeSemana(
                                'qtdPrimeirasHrs',
                                v.target.value,
                            )
                        }}
                    />
                    <FormField
                        size="small"
                        select
                        required
                        name={`horasExtras[${index}].porcentagemPrimeirasHrs`}
                        label="Será"
                        unHelperText
                        disabled={!editar || disabled}
                        onChange={v => {
                            handleChangeSemana(
                                'porcentagemPrimeirasHrs',
                                v.target.value,
                            )
                        }}
                    >
                        {porcentagem.map(item => (
                            <Menu.Item
                                key={item.id}
                                value={item.porcentagem}
                                disabled={disabled}
                            >
                                {item.porcentagem}%
                            </Menu.Item>
                        ))}
                    </FormField>
                </Card>
            </Grid>
            <Grid item sm={7} xs={10}>
                <Card>
                    <FormField
                        size="small"
                        select
                        disabled={!editar || isTodas(index) || disabled}
                        required={!isTodas(index)}
                        name={`horasExtras[${index}].porcentagemDemaisHrs`}
                        label={isTurno(index) ? 'Segundo turno' : 'Demais'}
                        unHelperText
                        onChange={v => {
                            handleChangeSemana(
                                'porcentagemDemaisHrs',
                                v.target.value,
                            )
                        }}
                    >
                        {porcentagem.map(item => (
                            <Menu.Item key={item.id} value={item.porcentagem}>
                                {item.porcentagem}%
                            </Menu.Item>
                        ))}
                    </FormField>
                </Card>
            </Grid>
            <Grid item sm={5} xs={20}>
                <Card>
                    <FormField
                        name={`horasExtras[${index}].prioridadeCompensacao`}
                        required
                        select
                        size="small"
                        label="Prioridade"
                        defaultValue="maior"
                        unHelperText
                        disabled={!editar || disabled}
                        onChange={v => {
                            handleChangeSemana(
                                'prioridadeCompensacao',
                                v.target.value,
                            )
                        }}
                    >
                        <Menu.Item value="maior">Maior</Menu.Item>
                        <Menu.Item value="menor">Menor</Menu.Item>
                    </FormField>
                </Card>
            </Grid>
            {children}
        </Grid>
    )
}

function CheckboxControlePorTurno({
    dia,
    disabled,
}: {
    dia: string
    disabled?: boolean
}) {
    const { watch, setValue } = useFormContext()
    const todas = watch(`horasExtras[${dia}].todas`, false)

    function handleChangeSemana(field: string, value: any) {
        if (dia === 'sem') {
            DIAS_UTEIS.forEach(dia => {
                setValue(`horasExtras[${dia}].${field}`, value)
            })
        }
    }

    return (
        <div className="inline-flex items-center">
            <FormFieldCheckbox
                label="Controle por turno"
                name={`horasExtras[${dia}].controleTurno`}
                disabled={disabled || todas}
                onChange={v => {
                    handleChangeSemana('controleTurno', v.target.checked)
                }}
            />
            <IconeAjuda className="-ml-2">
                {todas && ['Desabilite a opção "Todas" para alterar.', <br />]}
                Se habilitado, o controle de horas extras será feito por turno
                (manhã e tarde) ao invés de primeiras e demais horas.
                <br />A divisão de turnos ocorre ao meio-dia.
            </IconeAjuda>
        </div>
    )
}
